/**
 * "Recently viewed" keeps product ids only. The details come from the backend in
 * the current language, with today's price and availability. Storing whole
 * products left Greek names on the English page and old prices on both.
 */
export const RECENT_PRODUCT_IDS_KEY = 'botilia_recent_product_ids';
/** Earlier versions stored the whole product here. */
export const LEGACY_RECENT_PRODUCTS_KEY = 'botilia_recent_products';
export const MAX_RECENT_PRODUCTS = 8;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function getLocalStorageSafe(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function parseArray(raw: string | null): unknown[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveRecentProductIds(storage: StorageLike | null, ids: string[]): void {
  try {
    storage?.setItem(RECENT_PRODUCT_IDS_KEY, JSON.stringify(ids.slice(0, MAX_RECENT_PRODUCTS)));
  } catch {
    /* storage blocked or full: the list is a convenience */
  }
}

/** Reads the ids, moving a list saved by an earlier version over to the new format once. */
export function loadRecentProductIds(storage: StorageLike | null): string[] {
  if (!storage) return [];
  try {
    const current = storage.getItem(RECENT_PRODUCT_IDS_KEY);
    if (current !== null) {
      return parseArray(current)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
        .slice(0, MAX_RECENT_PRODUCTS);
    }

    const legacy = storage.getItem(LEGACY_RECENT_PRODUCTS_KEY);
    if (legacy === null) return [];
    const ids = parseArray(legacy)
      .map((p) => (p && typeof p === 'object' ? (p as { id?: unknown }).id : undefined))
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
      .slice(0, MAX_RECENT_PRODUCTS);
    saveRecentProductIds(storage, ids);
    storage.removeItem(LEGACY_RECENT_PRODUCTS_KEY);
    return ids;
  } catch {
    return [];
  }
}

/** The list after viewing `id`: most recent first, no duplicates, capped. */
export function addRecentProductId(ids: string[], id: string): string[] {
  return [id, ...ids.filter((existing) => existing !== id)].slice(0, MAX_RECENT_PRODUCTS);
}
